import {
  type FC,
  useState,
  useMemo,
  useEffect,
  useCallback,
  useRef,
} from "react";
import Modal from "@via/components/modal.tsx";
import { ModalSize } from "@via/utils/constants.tsx";
import { Spinner } from "@via/components/loading/loading.tsx";
import Cart from "@via/pages/data/data-sources/cloud-data-sources/common/file-browser/cart.tsx";
import {
  BucketObject,
  BucketObjectTypes,
  keyOf,
} from "@via/pages/data/data-sources/utils/helpers.tsx";
import { useNotifications } from "@via/hooks/useNotifications.tsx";
import { HorizontalTabs } from "@via/components/tabs.tsx";
import { HeadingSize, PageHeading } from "@via/components/page-heading";
import {
  MessageBanner,
  BannerType,
  BannerSize,
} from "@via/components/message-banner/message-banner.tsx";
import {
  ArrowDownTrayIcon,
  CheckCircleIcon,
  ClipboardDocumentIcon,
  CheckIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  StopCircleIcon,
} from "@heroicons/react/24/outline";
import {
  type DownloadStatus,
  type DownloadProgress,
  getDownloadStatusIcon,
  getDownloadStatusText,
} from "@via/pages/projects/runs/downloads/download-utils.tsx";
import {
  useAmazonS3FolderList,
  useGoogleCloudFolderList,
} from "@via/pages/data/data-sources/data-source.api.tsx";
import {
  DataSourceProvider,
  type S3ConnectionCredentials,
  type GCSConnectionCredentials,
  type FolderListDetails,
} from "common";
import {
  useGenerateVmetaDownloadUrls,
  useSendDatasetDownloadNotification,
} from "@via/pages/data/premium-dataset/premium-dataset.api.tsx";
import { EmptyDataIcon } from "@via/utils/custom-icons.tsx";
import {
  parseCredentialId,
  hasWildcard,
  looksLikeFile,
  parseCloudPath,
  computeCommonPrefix,
  getRelativePath,
} from "@via/pages/data/premium-dataset/utils/helper.ts";
import { getAxiosErrorMessage } from "@via/utils/helpers.tsx";
import Config from "@via/utils/config";
import {
  browserHref,
  isNavigableDownload,
  isScriptable,
  needsStreamAuth,
  unixDownloadCommand,
  windowsDownloadCommand,
  windowsMkdirCommand,
  commentText,
  safeRelativePath,
  NO_SCRIPTABLE_LINKS,
  STREAM_AUTH_NOTE_UNIX,
  STREAM_AUTH_NOTE_WINDOWS,
} from "@via/pages/projects/runs/downloads/download-script";

interface VmetaDownloadCartModalProps {
  isOpen: boolean;
  onClose: () => void;
  datasetFiles: Record<string, unknown>[];
  datasetName?: string;
  /** The dataset the cart's files are registered on; the server refuses S3 files without it (aws-2-3). */
  datasetId?: string;
}

type ScriptType = "Unix/Linux/macOS" | "Windows (PowerShell)";

// Maximum files to resolve from wildcard patterns (prevents performance issues)
const MAX_RESOLVED_FILES = 1000;

// Extract unique file paths from file1-file4 columns with their credential IDs
// Returns a Map where key is the path and value is the credential ID (-1 = IAM role)
const extractFilePathsWithCredentials = (
  files: Record<string, unknown>[]
): { paths: string[]; credentialsByPath: Map<string, number> } => {
  const credentialsByPath = new Map<string, number>();

  files.forEach(file => {
    const googleCreId = parseCredentialId(file.google_cre_id);
    const amazonCreId = parseCredentialId(file.amazon_cre_id);

    ["file1", "file2", "file3", "file4"].forEach(key => {
      const value = file[key];
      if (value && typeof value === "string" && value.trim()) {
        value.split(",").forEach(path => {
          const trimmedPath = path.trim();
          if (trimmedPath && !credentialsByPath.has(trimmedPath)) {
            if (trimmedPath.startsWith("gs://")) {
              credentialsByPath.set(trimmedPath, googleCreId);
            } else if (trimmedPath.startsWith("s3://")) {
              credentialsByPath.set(trimmedPath, amazonCreId);
            }
          }
        });
      }
    });
  });

  return { paths: Array.from(credentialsByPath.keys()), credentialsByPath };
};

const VmetaDownloadCartModal: FC<VmetaDownloadCartModalProps> = ({
  isOpen,
  onClose,
  datasetFiles,
  datasetName,
  datasetId,
}) => {
  const { setNotification, NOTIFICATION } = useNotifications();
  const [cart, setCart] = useState<BucketObject[]>([]);
  const [isResolvingPaths, setIsResolvingPaths] = useState(false);
  const [resolveErrors, setResolveErrors] = useState<string[]>([]);
  const [showDownloadOptions, setShowDownloadOptions] = useState(false);
  const [downloadUrls, setDownloadUrls] = useState<
    {
      fileName: string;
      url: string;
      method: "direct" | "proxy" | "stream";
      originalPath: string;
    }[]
  >([]);
  const [downloadProgress, setDownloadProgress] = useState<DownloadProgress[]>(
    []
  );
  const [isDownloading, setIsDownloading] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [scriptType, setScriptType] = useState<ScriptType>("Unix/Linux/macOS");
  const [copied, setCopied] = useState(false);
  const [showScript, setShowScript] = useState(false);
  const [scriptUrls, setScriptUrls] = useState<typeof downloadUrls | null>(
    null
  );
  const cancelRef = useRef(false);
  const [credentialsByPath, setCredentialsByPath] = useState<
    Map<string, number>
  >(new Map());

  const { mutateAsync: getAmazonFolderList } = useAmazonS3FolderList();
  const { mutateAsync: getGoogleFolderList } = useGoogleCloudFolderList();
  const { mutateAsync: generateDownloadUrls, isPending: isGeneratingUrls } =
    useGenerateVmetaDownloadUrls();
  const { mutate: sendDownloadNotification } =
    useSendDatasetDownloadNotification();
  // Extract and resolve file paths when modal opens
  useEffect(() => {
    if (!isOpen || datasetFiles.length === 0) {
      setCart([]);
      setCredentialsByPath(new Map());
      setScriptUrls(null);
      return;
    }
    setScriptUrls(null);

    const resolvePaths = async () => {
      setIsResolvingPaths(true);
      setResolveErrors([]);

      try {
        const { paths: rawPaths, credentialsByPath: pathCredentials } =
          extractFilePathsWithCredentials(datasetFiles);
        // Create a mutable copy of credentials map to add resolved paths
        const updatedCredentials = new Map(pathCredentials);
        const resolvedFiles: BucketObject[] = [];
        const errors: string[] = [];

        for (const path of rawPaths) {
          // Stop processing if we've hit the maximum file limit
          if (resolvedFiles.length >= MAX_RESOLVED_FILES) {
            errors.push(
              `Maximum file limit (${MAX_RESOLVED_FILES}) reached. Remaining paths were not processed.`
            );
            break;
          }

          // Get credentials from the original path (for wildcard paths, these will be inherited)
          const originalPathCreds = pathCredentials.get(path);

          const fileStatus = looksLikeFile(path);
          const hasWildcardInPath = hasWildcard(path);

          const parsed = parseCloudPath(path);
          if (!parsed) {
            errors.push(`Invalid path format: ${path}`);
            continue;
          }

          let dirPath: string;
          let pattern: string;

          let needsRecursiveListing = true;

          if (hasWildcardInPath) {
            const wildcardIndex = path.indexOf("*");
            const lastSlashBeforeWildcard = path.lastIndexOf(
              "/",
              wildcardIndex
            );
            dirPath = path.substring(0, lastSlashBeforeWildcard);
            pattern = path.substring(lastSlashBeforeWildcard + 1);
          } else if (fileStatus === true) {
            const lastSlash = path.lastIndexOf("/");
            dirPath = path.substring(0, lastSlash);
            pattern = path.substring(lastSlash + 1);
            needsRecursiveListing = false;
          } else if (fileStatus === false) {
            dirPath = path.slice(0, -1);
            pattern = "*";
          } else {
            dirPath = path;
            pattern = "*";
          }

          try {
            const dirParsed = parseCloudPath(dirPath);
            if (!dirParsed) {
              errors.push(`Invalid derived directory path for: ${path}`);
              continue;
            }

            let fileList: FolderListDetails | undefined;
            if (dirParsed.provider === DataSourceProvider.Google) {
              fileList = await getGoogleFolderList({
                savedCredentialId: originalPathCreds ?? -1,
                bucketName: dirParsed.bucket,
                prefix: dirParsed.prefix ? dirParsed.prefix + "/" : undefined,
                recursive: needsRecursiveListing,
              } as GCSConnectionCredentials);
            } else if (dirParsed.provider === DataSourceProvider.Amazon) {
              fileList = await getAmazonFolderList({
                savedCredentialId: originalPathCreds ?? -1,
                bucketName: dirParsed.bucket,
                prefix: dirParsed.prefix ? dirParsed.prefix + "/" : undefined,
                recursive: needsRecursiveListing,
              } as S3ConnectionCredentials);
            }

            // Convert glob pattern to regex using single-pass to avoid mangling
            // Note: */ becomes (.*\/)? to optionally match a folder level
            // e.g., */file.txt matches both "subfolder/file.txt" AND "file.txt"
            const regexPattern = pattern.replace(
              /(\*\/)|(\*)|(\?)|([.+^${}()|[\]\\])/g,
              (match, starSlash, star, question, special) => {
                if (starSlash) return "(.*/)?"; // */ → optional folder match
                if (star) return ".*"; // * → match any characters
                if (question) return "."; // ? → match single character
                if (special) return "\\" + special; // Escape regex special chars
                return match;
              }
            );
            const regex = new RegExp(`^${regexPattern}$`);

            // Determine if we should get all files or just the first match
            // Pattern like "*" or ending with "/*" → get all files
            // Pattern like "*/file.txt" (wildcard in middle with specific filename) → get first match only
            const getAllFiles = pattern === "*" || pattern.endsWith("*");

            const contents = fileList?.Contents ?? [];
            let matchFound = false;
            let limitReached = false;

            // Get the prefix to calculate relative path
            const listPrefix = dirParsed.prefix ? dirParsed.prefix + "/" : "";

            for (const item of contents) {
              // Check if we've hit the maximum resolved files limit
              if (resolvedFiles.length >= MAX_RESOLVED_FILES) {
                limitReached = true;
                break;
              }

              const itemName = item.Key;
              const fileName = itemName.split("/").pop() || "";
              // Get relative path from the directory (for patterns like */file.txt)
              const relativePath = itemName.startsWith(listPrefix)
                ? itemName.substring(listPrefix.length)
                : itemName;

              // Match against relative path for patterns with "/" (like */file.txt)
              // Match against filename for simple patterns (like * or *.txt)
              const matchTarget = pattern.includes("/")
                ? relativePath
                : fileName;

              if (regex.test(matchTarget) && !itemName.endsWith("/")) {
                const fullPath =
                  dirParsed.provider === DataSourceProvider.Google
                    ? `gs://${dirParsed.bucket}/${itemName}`
                    : `s3://${dirParsed.bucket}/${itemName}`;

                // Inherit credential from the original wildcard path (default to -1 for IAM role)
                updatedCredentials.set(fullPath, originalPathCreds ?? -1);

                resolvedFiles.push({
                  id: fullPath,
                  name: fullPath,
                  displayName: fileName,
                  type: BucketObjectTypes.File,
                  size: Number(item.Size) || 0,
                  path: fullPath,
                  dateModified: item.LastModified || "",
                });
                matchFound = true;

                // If pattern specifies a specific file (not just "*"), take only the first match
                if (!getAllFiles) {
                  break;
                }
              }
            }

            if (limitReached) {
              errors.push(
                `Maximum file limit (${MAX_RESOLVED_FILES}) reached. Some files may not be included.`
              );
            } else if (!matchFound) {
              if (fileStatus === "uncertain") {
                const lastSlash = path.lastIndexOf("/");
                const parentPath =
                  lastSlash > 0 ? path.substring(0, lastSlash) : null;
                const targetName = path.substring(lastSlash + 1);
                const parentParsed = parentPath
                  ? parseCloudPath(parentPath)
                  : null;

                if (parentParsed) {
                  let parentList: FolderListDetails | undefined;
                  if (parentParsed.provider === DataSourceProvider.Google) {
                    parentList = await getGoogleFolderList({
                      savedCredentialId: originalPathCreds ?? -1,
                      bucketName: parentParsed.bucket,
                      prefix: parentParsed.prefix
                        ? parentParsed.prefix + "/"
                        : undefined,
                      recursive: false,
                    } as GCSConnectionCredentials);
                  } else if (
                    parentParsed.provider === DataSourceProvider.Amazon
                  ) {
                    parentList = await getAmazonFolderList({
                      savedCredentialId: originalPathCreds ?? -1,
                      bucketName: parentParsed.bucket,
                      prefix: parentParsed.prefix
                        ? parentParsed.prefix + "/"
                        : undefined,
                      recursive: false,
                    } as S3ConnectionCredentials);
                  }

                  const found = (parentList?.Contents ?? []).find(item => {
                    const key = item.Key ?? "";
                    const name = key.split("/").pop() || "";
                    return name === targetName && !key.endsWith("/");
                  });

                  if (found) {
                    updatedCredentials.set(path, originalPathCreds ?? -1);
                    resolvedFiles.push({
                      id: path,
                      name: path,
                      displayName: targetName,
                      type: BucketObjectTypes.File,
                      size: Number(found.Size) || 0,
                      path: path,
                      dateModified: found.LastModified || "",
                    });
                  } else {
                    errors.push(`File not found: ${path}`);
                  }
                } else {
                  errors.push(`File not found: ${path}`);
                }
              } else if (fileStatus === true) {
                errors.push(`File not found: ${path}`);
              } else {
                errors.push(`No files found matching pattern: ${path}`);
              }
            }
          } catch (err) {
            console.error(`Error resolving path ${path}:`, err);
            errors.push(
              getAxiosErrorMessage(err) ?? `Failed to resolve: ${path}`
            );
          }
        }

        // Deduplicate by path
        const uniqueFiles = resolvedFiles.filter(
          (file, index, self) =>
            self.findIndex(f => f.name === file.name) === index
        );

        setCredentialsByPath(updatedCredentials);
        setCart(uniqueFiles);
        setResolveErrors(errors);
      } catch (err) {
        console.error("Error extracting file paths:", err);
        setResolveErrors(["Failed to extract file paths from dataset"]);
      } finally {
        setIsResolvingPaths(false);
      }
    };

    resolvePaths();
  }, [isOpen, datasetFiles, getAmazonFolderList, getGoogleFolderList]);

  const removeFromCart = useCallback((key: string) => {
    setCart(prev => prev.filter(item => keyOf(item) !== key));
  }, []);

  const clearCart = useCallback(() => {
    setCart([]);
  }, []);

  const prepareFilesForDownload = useCallback(() => {
    return cart.map(item => {
      const fileName =
        item.displayName || item.name.split("/").pop() || item.name;
      const fullPath = item.name;
      const credId = credentialsByPath.get(fullPath) ?? -1;

      return {
        name: fileName,
        path: fullPath,
        size: Number(item.size) || 0,
        ...(fullPath.startsWith("gs://") && { googleCreId: credId }),
        ...(fullPath.startsWith("s3://") && { amazonCreId: credId }),
      };
    });
  }, [cart, credentialsByPath]);

  const handleContinue = useCallback(async () => {
    if (cart.length === 0) return;

    try {
      const files = prepareFilesForDownload();

      const result = await generateDownloadUrls({
        files,
        downloadMode: "browser",
        datasetName,
        datasetId,
      });

      if (result && result.urls) {
        const urlsWithPaths = result.urls.map(urlInfo => ({
          ...urlInfo,
          originalPath: urlInfo.path,
        }));
        setDownloadUrls(urlsWithPaths);
        setShowDownloadOptions(true);
      }
    } catch (err) {
      console.error("Error generating download URLs:", err);
      setNotification({
        id: "generate-urls-failed",
        message: "Failed to generate download URLs",
        details:
          getAxiosErrorMessage(err) ?? "Please try again or contact support.",
        variant: NOTIFICATION.ERROR,
      });
    }
  }, [
    cart,
    prepareFilesForDownload,
    generateDownloadUrls,
    datasetName,
    datasetId,
    setNotification,
    NOTIFICATION,
  ]);

  const ensureScriptUrls = useCallback(async () => {
    if (scriptUrls) return scriptUrls;
    if (cart.length === 0) return downloadUrls;

    try {
      const files = prepareFilesForDownload();

      const result = await generateDownloadUrls({
        files,
        downloadMode: "script",
        datasetId,
      });
      if (result?.urls) {
        const urlsWithPaths = result.urls.map(urlInfo => ({
          ...urlInfo,
          originalPath: urlInfo.path,
        }));
        setScriptUrls(urlsWithPaths);
        return urlsWithPaths;
      }
      return downloadUrls;
    } catch {
      return downloadUrls;
    }
  }, [
    scriptUrls,
    cart,
    prepareFilesForDownload,
    generateDownloadUrls,
    downloadUrls,
    datasetId,
  ]);

  const getScriptUrls = useCallback(() => {
    return scriptUrls ?? downloadUrls;
  }, [scriptUrls, downloadUrls]);

  // Compute common prefix from download URLs' original paths for scripts
  const scriptCommonPrefix = useMemo(() => {
    const paths = downloadUrls.map(u => u.originalPath);
    return computeCommonPrefix(paths);
  }, [downloadUrls]);

  const generateUnixScript = useCallback(
    (urlsOverride?: typeof downloadUrls) => {
      const header = `# Download script for Unix/Linux/macOS\n# Generated by Foundry Connect\n# Dataset: ${commentText(datasetName || "Unknown")}\n\n`;

      const urls = (urlsOverride ?? getScriptUrls()).filter(isScriptable);
      if (urls.length === 0) return header + NO_SCRIPTABLE_LINKS;
      const commands = urls
        .map((url, index) => {
          const relativePath = safeRelativePath(
            getRelativePath(url.originalPath, scriptCommonPrefix),
            url.fileName
          );
          return `# File ${index + 1}: ${commentText(relativePath)}\n${unixDownloadCommand(url, relativePath, window.location.origin, Config.ROUTER_BASENAME ?? "")}`;
        })
        .join("\n\n");

      return (
        header + (needsStreamAuth(urls) ? STREAM_AUTH_NOTE_UNIX : "") + commands
      );
    },
    [getScriptUrls, scriptCommonPrefix, datasetName]
  );

  const generateWindowsScript = useCallback(
    (urlsOverride?: typeof downloadUrls) => {
      const header = `# Download script for Windows PowerShell\n# Generated by Foundry Connect\n# Dataset: ${commentText(datasetName || "Unknown")}\n\n`;

      const urls = (urlsOverride ?? getScriptUrls()).filter(isScriptable);
      if (urls.length === 0) return header + NO_SCRIPTABLE_LINKS;
      const commands = urls
        .map((url, index) => {
          const relativePath = safeRelativePath(
            getRelativePath(url.originalPath, scriptCommonPrefix),
            url.fileName
          );
          const winFilePath = relativePath.replace(/\//g, "\\");
          const mkdirCmd = windowsMkdirCommand(winFilePath);
          return `# File ${index + 1}: ${commentText(relativePath)}\n${mkdirCmd}${windowsDownloadCommand(url, winFilePath, window.location.origin, Config.ROUTER_BASENAME ?? "")}`;
        })
        .join("\n\n");

      return (
        header +
        (needsStreamAuth(urls) ? STREAM_AUTH_NOTE_WINDOWS : "") +
        commands
      );
    },
    [getScriptUrls, scriptCommonPrefix, datasetName]
  );

  const handleCopyScript = useCallback(async () => {
    const resolvedUrls = (await ensureScriptUrls()) ?? getScriptUrls();
    const script =
      scriptType === "Unix/Linux/macOS"
        ? generateUnixScript(resolvedUrls)
        : generateWindowsScript(resolvedUrls);
    if (navigator?.clipboard) {
      try {
        await navigator.clipboard.writeText(script);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        console.error("Failed to copy script:", err);
      }
    }
  }, [
    scriptType,
    generateUnixScript,
    generateWindowsScript,
    ensureScriptUrls,
    getScriptUrls,
  ]);

  const handleDownloadScript = useCallback(
    async (type: ScriptType) => {
      const resolvedUrls = (await ensureScriptUrls()) ?? getScriptUrls();
      setScriptType(type);
      const isUnix = type === "Unix/Linux/macOS";
      const script = isUnix
        ? generateUnixScript(resolvedUrls)
        : generateWindowsScript(resolvedUrls);
      const extension = isUnix ? "sh" : "ps1";

      const blob = new Blob([script], { type: "text/plain" });
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = `download-${datasetName || "files"}.${extension}`;
      link.click();
      window.URL.revokeObjectURL(blobUrl);
    },
    [
      datasetName,
      generateUnixScript,
      generateWindowsScript,
      ensureScriptUrls,
      getScriptUrls,
    ]
  );

  const startDirectDownload = useCallback(async () => {
    if (datasetName) {
      sendDownloadNotification({ datasetName });
    }

    setIsDownloading(true);
    setIsCancelling(false);
    cancelRef.current = false;

    try {
      const initialProgress: DownloadProgress[] = downloadUrls.map(urlInfo => ({
        fileName: urlInfo.fileName,
        status: "pending" as DownloadStatus,
      }));
      setDownloadProgress(initialProgress);

      let successCount = 0;
      let failCount = 0;

      for (const [i, urlInfo] of downloadUrls.entries()) {
        if (cancelRef.current) {
          setDownloadProgress(prev =>
            prev.map((item, idx) =>
              idx >= i ? { ...item, status: "cancelled" } : item
            )
          );
          break;
        }

        setDownloadProgress(prev =>
          prev.map((item, idx) =>
            idx === i ? { ...item, status: "downloading" } : item
          )
        );

        try {
          const fileName = urlInfo.fileName.includes("/")
            ? urlInfo.fileName.substring(urlInfo.fileName.lastIndexOf("/") + 1)
            : urlInfo.fileName;

          if (isNavigableDownload(urlInfo)) {
            const link = document.createElement("a");
            link.href = browserHref(urlInfo, Config.ROUTER_BASENAME ?? "");
            link.download = fileName;
            link.style.display = "none";
            document.body.appendChild(link);
            await new Promise(resolve => requestAnimationFrame(resolve));
            link.click();
            await new Promise(resolve => requestAnimationFrame(resolve));
            document.body.removeChild(link);
            await new Promise(resolve => setTimeout(resolve, 1000));
          } else {
            const response = await fetch(urlInfo.url);
            if (!response.ok)
              throw new Error(`Failed to download ${urlInfo.fileName}`);
            const blob = await response.blob();
            const blobUrl = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = blobUrl;
            link.download = fileName;
            link.style.display = "none";
            document.body.appendChild(link);
            await new Promise(resolve => requestAnimationFrame(resolve));
            link.click();
            await new Promise(resolve => requestAnimationFrame(resolve));
            document.body.removeChild(link);
            window.URL.revokeObjectURL(blobUrl);
            await new Promise(resolve => setTimeout(resolve, 1000));
          }

          setDownloadProgress(prev =>
            prev.map((item, idx) =>
              idx === i ? { ...item, status: "completed" } : item
            )
          );
          successCount++;
        } catch (error) {
          console.error(`Failed to download ${urlInfo.fileName}:`, error);
          setDownloadProgress(prev =>
            prev.map((item, idx) =>
              idx === i
                ? {
                    ...item,
                    status: "failed",
                    error:
                      error instanceof Error ? error.message : "Unknown error",
                  }
                : item
            )
          );
          failCount++;
        }
      }

      if (successCount > 0 && failCount === 0) {
        setNotification({
          id: "download-complete",
          message: "Downloads Initiated",
          details: `Successfully initiated ${successCount} download${successCount > 1 ? "s" : ""}. Check your browser's downloads folder.`,
          variant: NOTIFICATION.SUCCESS,
        });
      } else if (successCount > 0 && failCount > 0) {
        setNotification({
          id: "download-partial",
          message: "Partial Download",
          details: `Initiated ${successCount} download${successCount > 1 ? "s" : ""}, ${failCount} failed.`,
          variant: NOTIFICATION.WARNING,
        });
      } else if (failCount > 0) {
        setNotification({
          id: "download-failed",
          message: "Download Failed",
          details:
            "Failed to initiate downloads. Please try again or use the download script.",
          variant: NOTIFICATION.ERROR,
        });
      }
    } finally {
      setIsDownloading(false);
      setIsCancelling(false);
      cancelRef.current = false;
    }
  }, [
    downloadUrls,
    datasetName,
    sendDownloadNotification,
    setNotification,
    NOTIFICATION,
  ]);

  const handleCancel = useCallback(() => {
    if (isDownloading && !isCancelling) {
      setIsCancelling(true);
      cancelRef.current = true;
    } else {
      setShowDownloadOptions(false);
      setDownloadProgress([]);
      setDownloadUrls([]);
      setScriptUrls(null);
      setScriptType("Unix/Linux/macOS");
      setCopied(false);
      setShowScript(false);
      onClose();
    }
  }, [isDownloading, isCancelling, onClose]);

  const getCompletedCount = () => {
    return downloadProgress.filter(item => item.status === "completed").length;
  };

  const showScripts = downloadUrls.length > 0 && downloadProgress.length === 0;

  return (
    <Modal
      open={isOpen}
      setOpen={handleCancel}
      title={
        <div className="flex items-center gap-2">
          <span className="text-lg font-semibold">
            {showDownloadOptions
              ? showScripts
                ? "Download Files"
                : "Download Progress"
              : "Download Cart"}
          </span>
          {!showDownloadOptions && cart.length > 0 && (
            <span className="text-sm text-gray-500">({cart.length} files)</span>
          )}
          {!showDownloadOptions && downloadProgress.length > 0 && (
            <span className="text-sm text-gray-600">
              {getCompletedCount()} of {downloadProgress.length} initiated
            </span>
          )}
        </div>
      }
      size={ModalSize.Large}
      hasCancelButton={true}
      cancelText={isDownloading && !isCancelling ? "Cancel" : "Close"}
      onCancel={handleCancel}
      disableClose={isDownloading && isCancelling}
      minHeight={400}>
      {isResolvingPaths ? (
        <div className="flex flex-col items-center justify-center py-12">
          <Spinner loading={true} />
          <p className="mt-4 text-sm text-gray-600">Extracting file paths...</p>
        </div>
      ) : !showDownloadOptions ? (
        <div className="space-y-4 py-4">
          {resolveErrors.length > 0 && (
            <MessageBanner
              messages={resolveErrors.join("; ")}
              type={BannerType.Warning}
              title="Some paths could not be resolved"
              size={BannerSize.Small}
            />
          )}

          {cart.length === 0 ? (
            <div className="via-generic-error w-full justify-center py-8">
              <EmptyDataIcon />
              <p>No valid file paths found in the selected rows.</p>
              <p className="text-sm text-gray-500">
                Make sure the file1-file4 columns contain valid cloud paths
                (gs:// or s3://).
              </p>
            </div>
          ) : (
            <Cart
              items={cart}
              onRemove={removeFromCart}
              onClear={clearCart}
              onContinue={handleContinue}
              submitButtonLoading={isGeneratingUrls}
            />
          )}
        </div>
      ) : showScripts ? (
        <div className="space-y-6 pb-4">
          <p className="text-sm text-gray-700">
            Choose how you want to download the files:
          </p>
          <MessageBanner
            messages="Download links expire in 1 day. If expired, close this modal and initiate download again to generate new links."
            type={BannerType.Warning}
            title="Note"
            size={BannerSize.Small}
          />

          {/* Option A: Direct Browser Download */}
          <div className="px-4 py-3 shadow-md ring-1 ring-gray-900/5 rounded-lg">
            <div className="mb-3">
              <PageHeading
                title="Option A: Direct Browser Download"
                size={HeadingSize.XSmall}
              />
              <p className="mt-2 text-sm text-gray-600">
                Download all files directly to your browser&apos;s default
                download directory.
              </p>
              <p className="mt-1 text-sm font-bold text-gray-900">
                Note: Folder structure will not be preserved.
              </p>
            </div>

            <button
              onClick={startDirectDownload}
              disabled={isDownloading}
              className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-md disabled:bg-gray-400 disabled:cursor-not-allowed transition">
              <span className="w-5 h-5 flex items-center justify-center">
                {isDownloading ? (
                  <Spinner loading />
                ) : (
                  <ArrowDownTrayIcon className="w-5 h-5" />
                )}
              </span>
              Download Files Only (No Folder Structure)
            </button>
          </div>

          {/* Option B: Download with Script */}
          <div className="px-4 py-3 shadow-md ring-1 ring-gray-900/5 rounded-lg">
            <div className="mb-3">
              <PageHeading
                title="Option B: Download with Script (Preserves Folder Structure)"
                size={HeadingSize.XSmall}
              />
              <p className="mt-2 text-sm text-gray-600">
                Download and run the script to download all files while keeping
                the folder structure.
              </p>
            </div>

            <div className="flex gap-3 mb-3">
              <button
                onClick={() => handleDownloadScript("Unix/Linux/macOS")}
                disabled={isGeneratingUrls}
                className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-md disabled:bg-gray-400 disabled:cursor-not-allowed transition">
                <ArrowDownTrayIcon className="w-5 h-5" />
                Download Script (Unix/Linux/macOS)
              </button>
              <button
                onClick={() => handleDownloadScript("Windows (PowerShell)")}
                disabled={isGeneratingUrls}
                className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-md disabled:bg-gray-400 disabled:cursor-not-allowed transition">
                <ArrowDownTrayIcon className="w-5 h-5" />
                Download Script (Windows/PowerShell)
              </button>
            </div>

            {/* Show Script Section */}
            <div className="border-t border-gray-200 pt-3">
              <button
                onClick={async () => {
                  if (!showScript) await ensureScriptUrls();
                  setShowScript(!showScript);
                }}
                disabled={isGeneratingUrls}
                className="flex items-center gap-2 text-sm font-medium text-gray-700 hover:text-gray-900 disabled:opacity-50">
                {isGeneratingUrls && !showScript ? (
                  <>
                    <Spinner loading />
                    Loading Script URLs...
                  </>
                ) : showScript ? (
                  <>
                    <ChevronUpIcon className="h-4 w-4" />
                    Hide Script
                  </>
                ) : (
                  <>
                    <ChevronDownIcon className="h-4 w-4" />
                    Show Script
                  </>
                )}
              </button>

              {showScript && (
                <div className="mt-3 space-y-3">
                  <HorizontalTabs
                    options={[
                      { name: "Unix/Linux/macOS" as ScriptType },
                      { name: "Windows (PowerShell)" as ScriptType },
                    ]}
                    activeTab={scriptType}
                    onTabChange={setScriptType}
                  />

                  <div className="relative">
                    <textarea
                      readOnly
                      value={
                        scriptType === "Unix/Linux/macOS"
                          ? generateUnixScript()
                          : generateWindowsScript()
                      }
                      className="w-full h-64 font-mono text-xs p-3 bg-gray-900 text-gray-100 rounded-md resize-none focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                    <button
                      onClick={handleCopyScript}
                      className="absolute top-2 right-2 flex items-center gap-2 px-3 py-1.5 text-xs font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-md transition">
                      {copied ? (
                        <>
                          <CheckIcon className="w-4 h-4" />
                          Copied!
                        </>
                      ) : (
                        <>
                          <ClipboardDocumentIcon className="w-4 h-4" />
                          Copy Script
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className="space-y-4 pb-4">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600">
              {isCancelling ? (
                <span className="flex items-center gap-2 text-red-600">
                  <StopCircleIcon className="h-5 w-5" />
                  Cancelling downloads...
                </span>
              ) : isDownloading ? (
                <span className="flex items-center gap-2">
                  <ArrowDownTrayIcon className="h-5 w-5 animate-bounce text-indigo-600" />
                  Initiating downloads...
                </span>
              ) : (
                <span className="flex items-center gap-2 text-green-600">
                  <CheckCircleIcon className="h-5 w-5" />
                  All downloads initiated - check your browser&apos;s download
                  folder
                </span>
              )}
            </div>
          </div>
          <div className="max-h-96 overflow-y-auto">
            <div className="space-y-3">
              {downloadProgress.map((item, index) => (
                <div
                  key={index}
                  className="flex items-start gap-3 rounded-md border border-gray-200 bg-gray-50 p-3">
                  <div className="flex-shrink-0">
                    {getDownloadStatusIcon(item.status)}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium text-gray-900">
                      {item.fileName}
                    </div>
                    {item.error && (
                      <div className="mt-1 text-xs text-red-600">
                        {item.error}
                      </div>
                    )}
                    <div className="mt-1 text-xs text-gray-500">
                      {getDownloadStatusText(item.status)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default VmetaDownloadCartModal;
